/**
 * ChatNavigationContext - the single place AI navigation suggestions become
 * route changes.
 *
 * The provider owns useNavigate and registers a handler ref (same pattern as
 * AiChatActionContext). Consumers:
 *   - ActionFooter (AiStructuredMessage): 'open' actions whose payload is a
 *     NavigationActionPayload route here on CLICK (ask-mode UX).
 *   - useChatSession: live structured_payload events auto-navigate here when
 *     the run's mode is 'agent', or 'auto' with payload.autoNavigate.
 *
 * Navigation always passes { state: { openCopilotRail: true } } so AppShell can
 * open the module-chat rail pane on arrival - the conversation visibly "moves"
 * from the landing main pane to the destination page's right rail.
 */

import type { NavigationActionPayload } from '@protoss/shared';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveNavigationTarget } from './resolveNavigationTarget';

export type ChatNavigationHandler = (
  payload: NavigationActionPayload,
  origin: 'click' | 'auto'
) => void;

type NavigationHandlerRef = React.MutableRefObject<ChatNavigationHandler | undefined>;

const ChatNavigationContext = React.createContext<NavigationHandlerRef | null>(null);

export function ChatNavigationProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const handlerRef = React.useRef<ChatNavigationHandler | undefined>(undefined);
  handlerRef.current = (payload) => {
    const url = resolveNavigationTarget(payload.target);
    if (!url) return; // unknown target kind (older client) - no-op
    const { target } = payload;
    // Transcript hand-off (from /create-deal): seed the same router state the create
    // wizard uses so the deal page's auto-trigger runs analysis there in the chosen mode.
    const transcriptState =
      target.kind === 'deal' && (target.transcriptDocUuids?.length || target.transcriptDocUuid)
        ? {
            transcriptDocUuids: target.transcriptDocUuids,
            transcriptDocUuid: target.transcriptDocUuid,
            docFileName: target.docFileName,
            companyName: target.companyName,
            mode: target.analyzeMode,
          }
        : {};
    navigate(url, { state: { openCopilotRail: true, ...transcriptState } });
  };
  return (
    <ChatNavigationContext.Provider value={handlerRef}>{children}</ChatNavigationContext.Provider>
  );
}

/** Stable ref object; read `.current` at call-time. Null outside the provider. */
export function useChatNavigationRef(): NavigationHandlerRef | null {
  return React.useContext(ChatNavigationContext);
}
